import { useCallback, useEffect, useRef, useState } from 'react';

import {
  AuditLogEntry,
  OnboardingStepsAPI,
} from '@/services/onboardingStepsAPI';

// BH-67: graphy pages GET /api/admin/onboarding_step_audit_logs (default 100,
// max 200, newest first). Reading only the first answer, as both audit log
// dialogs did, silently dropped every older row past the first page.
export const AUDIT_LOGS_PER_PAGE = 100;

const useAuditLogPages = (open: boolean, workerId: number) => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [workerName, setWorkerName] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  // A reopen or another worker retires answers still on the way.
  const seq = useRef(0);

  const fetchPage = useCallback(
    async (nextPage: number) => {
      const mine = seq.current;
      const response = await OnboardingStepsAPI.getAuditLogs(
        workerId,
        undefined,
        undefined,
        { page: nextPage, per_page: AUDIT_LOGS_PER_PAGE },
      );
      if (mine !== seq.current || !response.success) return;
      const rows = response.data.audit_logs ?? [];
      setLogs((prev) => {
        if (nextPage === 1) return rows;
        // A change saved while the dialog is open pushes rows down a page,
        // so the next page can repeat the last one's tail.
        const seen = new Set(prev.map((l) => l.id));
        return [...prev, ...rows.filter((l) => !seen.has(l.id))];
      });
      setWorkerName(response.data.worker_name);
      // No `pagination`: a graphy that does not page sends every row at once.
      const p = response.pagination;
      setPage(p?.page ?? nextPage);
      setTotalPages(p?.total_pages ?? nextPage);
      setTotal(p?.total ?? response.data.total_logs ?? rows.length);
    },
    [workerId],
  );

  useEffect(() => {
    if (!open) return;
    seq.current += 1;
    const mine = seq.current;
    setLogs([]);
    setPage(1);
    setTotalPages(1);
    setTotal(0);
    setError(null);
    setMoreError(null);
    setLoading(true);
    fetchPage(1)
      .catch((err) => {
        if (mine !== seq.current) return;
        setError('Failed to load audit logs');
        console.error('Error fetching audit logs:', err);
      })
      .finally(() => mine === seq.current && setLoading(false));
  }, [open, fetchPage]);

  const hasMore = page < totalPages;
  const loadMore = () => {
    if (!hasMore || loadingMore) return;
    const mine = seq.current;
    setLoadingMore(true);
    setMoreError(null);
    fetchPage(page + 1)
      .catch((err) => {
        if (mine !== seq.current) return;
        setMoreError('Failed to load more audit logs');
        console.error('Error fetching audit logs:', err);
      })
      .finally(() => mine === seq.current && setLoadingMore(false));
  };

  return {
    logs,
    loading,
    loadingMore,
    error,
    moreError,
    workerName,
    total,
    hasMore,
    loadMore,
  };
};

export default useAuditLogPages;
